import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { ApiService } from '../api/api.service';
import {
  CartId,
  GetCartId$Request,
  GetCartId$Response,
  CreateShopcart$Request,
  CreateShopcart$Response,
  GetShopcart$Request,
  GetShopcart$Response,
  AddCartItem$Request,
  AddCartItem$Response,
  RemoveCartItem$Request,
  RemoveCartItem$Response,
  SubmitCart$Request,
  SubmitCart$Response,
  RemoveShopcart$Request,
  RemoveShopcart$Response
} from '@api/types';

import { map, tap } from 'rxjs/operators';

export type ShopcartGeneral =
  | GetShopcart$Response
  | AddCartItem$Response
  | RemoveCartItem$Response
  | SubmitCart$Response;

@Injectable()
export class ShopcartService {
  private defaultState: GetShopcart$Response = {
    provider: null,
    shopcartItemList: [],
    submittedAt: null,
    url: null
  };

  private defaultCartIdState: GetCartId$Response = {
    cartId: null
  };

  private cartIdState: GetCartId$Response = this.defaultCartIdState;
  private state: GetShopcart$Response = this.defaultState;

  private cartIdSubject: Subject<GetCartId$Response> = new BehaviorSubject<
    GetCartId$Response
  >(this.cartIdState);
  private shopcartSubject: Subject<GetShopcart$Response> = new BehaviorSubject<
    GetShopcart$Response
  >(this.state);

  private cartIdIsLoading: boolean;
  private isLoading: boolean;

  constructor(private api: ApiService) {}

  setCartIdState(newState: GetCartId$Response) {
    if (this.cartIdState.cartId === newState.cartId) {
      return;
    }
    this.cartIdState = newState;
    this.cartIdSubject.next(this.cartIdState);
  }

  getCartIdState() {
    return this.cartIdSubject;
  }

  setState(newState: ShopcartGeneral) {
    this.state = { ...this.state, ...newState };
    this.shopcartSubject.next(this.state);
  }

  getState() {
    return this.shopcartSubject;
  }

  getCartId(
    request: GetCartId$Request,
    forceFetch: boolean = false
  ): Observable<GetCartId$Response> {
    if (forceFetch || !this.cartIdState.cartId) {
      if (!this.cartIdIsLoading) {
        this.getCartIdLatest(request).subscribe();
      }
    }
    return this.cartIdSubject;
  }

  getCartIdLatest(request: GetCartId$Request) {
    this.cartIdIsLoading = true;
    return this.api.shopcart.getCartId(request).pipe(
      tap(cartId => this.setCartIdState(cartId)),
      tap(({ cartId }) => {
        if (cartId) {
          this.getShopcartLatest({ cartId }).subscribe();
        }
      }),
      tap(() => (this.cartIdIsLoading = false))
    );
  }

  createShopCart(
    request: CreateShopcart$Request
  ): Observable<CreateShopcart$Response> {
    return this.api.shopcart
      .createShopcart(request)
      .pipe(tap(cartId => this.setCartIdState(cartId)));
  }

  getShopcart(
    request: GetShopcart$Request,
    forceFetch: boolean = false
  ): Observable<GetShopcart$Response> {
    if (forceFetch || !this.cartIdState.cartId) {
      if (!this.isLoading) {
        this.getShopcartLatest(request).subscribe();
      }
    }
    return this.shopcartSubject;
  }

  getShopcartLatest(
    request: GetShopcart$Request
  ): Observable<GetShopcart$Response> {
    this.isLoading = true;
    return this.api.shopcart.getShopcart(request).pipe(
      tap(shopcart => this.setState(shopcart)),
      tap(() => (this.isLoading = false))
    );
  }

  addCartItem(request: AddCartItem$Request): Observable<AddCartItem$Response> {
    return this.api.shopcart
      .addCartItem(request)
      .pipe(tap(shopcart => this.setState(shopcart)));
  }

  removeCartItem(
    request: RemoveCartItem$Request
  ): Observable<RemoveCartItem$Response> {
    return this.api.shopcart
      .removeCartItem(request)
      .pipe(tap(shopcart => this.setState(shopcart)));
  }

  submitCart(request: SubmitCart$Request): Observable<SubmitCart$Response> {
    return this.api.shopcart
      .submitCart(request)
      .pipe(tap(shopcart => this.setState(shopcart)));
  }

  removeShopcart(
    request: RemoveShopcart$Request
  ): Observable<RemoveShopcart$Response> {
    this.resetState();
    return this.api.shopcart.removeShopcart(request);
  }

  resetState() {
    this.setCartIdState(this.defaultCartIdState);
    this.setState(this.defaultState);
  }
}
